/**
 * Expense Report Detail Modal Component
 *
 * Displays full expense report details in a modal including:
 * - Report header with status and submission info
 * - Budget progress
 * - Receipts table with all details
 * - Receipt images (clickable to open lightbox)
 * - Read-only controls (for Department Head view)
 */

import type { ExpenseReportDetail } from '../types/expense-tracker';
import { formatCurrency } from '../types/expense-tracker';
import { createExpenseStatusBadge } from './expense-status-badge';
import { createBudgetProgress } from './budget-progress';
import { openImageLightbox } from './image-lightbox';
import { renderIcon } from '../utils/icons';

/**
 * Options for expense report detail modal
 */
export interface ExpenseReportDetailModalOptions {
  /** Whether the view is read-only (e.g., Department Head view) */
  readOnly?: boolean;
  /** Callback when modal is closed */
  onClose?: () => void;
}

/**
 * Create and show expense report detail modal
 * @param report - The expense report detail data
 * @param options - Modal options
 */
export function showExpenseReportDetailModal(
  report: ExpenseReportDetail,
  options: ExpenseReportDetailModalOptions = {}
): void {
  const { readOnly = false, onClose } = options;

  // Create modal overlay
  const overlay = document.createElement('div');
  overlay.className = `
    fixed inset-0 z-40 flex items-center justify-center
    bg-black/50 backdrop-blur-sm
    animate-in fade-in duration-200
  `;
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', 'Expense Report Details');

  // Modal container
  const modal = document.createElement('div');
  modal.className = `
    bg-card border border-border rounded-lg shadow-2xl
    w-full max-w-4xl max-h-[90vh]
    flex flex-col
    animate-in zoom-in-95 duration-200
  `;

  // Header
  const header = document.createElement('div');
  header.className = 'flex items-center justify-between p-6 border-b border-border';

  const titleSection = document.createElement('div');
  const title = document.createElement('h2');
  title.className = 'text-xl font-semibold';
  title.textContent = 'Expense Report Details';
  titleSection.appendChild(title);

  if (readOnly) {
    const readOnlyBadge = document.createElement('span');
    readOnlyBadge.className = 'text-xs text-muted-foreground mt-1 block';
    readOnlyBadge.textContent = 'Read-only view';
    titleSection.appendChild(readOnlyBadge);
  }

  const closeButton = document.createElement('button');
  closeButton.className = `
    p-2 rounded-md
    hover:bg-accent
    text-muted-foreground hover:text-foreground
    transition-colors
  `;
  closeButton.setAttribute('aria-label', 'Close');
  const closeIcon = renderIcon('x', { size: 'md' });
  closeButton.appendChild(closeIcon);

  header.appendChild(titleSection);
  header.appendChild(closeButton);
  modal.appendChild(header);

  // Content (scrollable)
  const content = document.createElement('div');
  content.className = 'flex-1 overflow-y-auto p-6 space-y-6';

  // Report info section
  const infoSection = document.createElement('div');
  infoSection.className = 'space-y-4';

  // Status and submission info
  const statusRow = document.createElement('div');
  statusRow.className = 'flex items-center justify-between';

  const statusBadge = createExpenseStatusBadge(report.status, { size: 'md' });
  statusRow.appendChild(statusBadge);

  const submittedText = document.createElement('span');
  submittedText.className = 'text-sm text-muted-foreground';
  if (report.submitted_at) {
    const submittedDate = new Date(report.submitted_at).toLocaleDateString('en-CH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
    submittedText.textContent = `Submitted ${submittedDate}`;
  } else {
    submittedText.textContent = 'Not submitted yet';
  }
  statusRow.appendChild(submittedText);

  infoSection.appendChild(statusRow);

  // Budget progress (if available)
  if (report.budget) {
    const budgetSection = document.createElement('div');
    const budgetTitle = document.createElement('h3');
    budgetTitle.className = 'text-sm font-medium mb-2';
    budgetTitle.textContent = 'Budget Status';
    budgetSection.appendChild(budgetTitle);

    const budgetProgress = createBudgetProgress({
      approvedAmount: report.budget.approved,
      spentAmount: report.budget.spent,
      currency: report.budget.currency,
    });
    budgetSection.appendChild(budgetProgress);

    infoSection.appendChild(budgetSection);
  }

  content.appendChild(infoSection);

  // Receipts section
  const receiptsSection = document.createElement('div');
  const receiptsTitle = document.createElement('h3');
  receiptsTitle.className = 'text-lg font-semibold mb-4';
  receiptsTitle.textContent = `Receipts (${report.receipts.length})`;
  receiptsSection.appendChild(receiptsTitle);

  if (report.receipts.length === 0) {
    const emptyState = document.createElement('p');
    emptyState.className = 'text-center py-8 text-muted-foreground';
    emptyState.textContent = 'No receipts added yet';
    receiptsSection.appendChild(emptyState);
  } else {
    // Receipts table
    const receiptsContainer = document.createElement('div');
    receiptsContainer.className = 'space-y-4';

    for (const receipt of report.receipts) {
      const receiptCard = document.createElement('div');
      receiptCard.className = 'border border-border rounded-lg p-4 space-y-3';

      // Receipt image thumbnail (clickable)
      if (receipt.stored_filename) {
        const imageThumbnail = document.createElement('div');
        imageThumbnail.className = 'flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity';
        imageThumbnail.setAttribute('role', 'button');
        imageThumbnail.setAttribute('tabindex', '0');

        const thumbnailImg = document.createElement('img');
        thumbnailImg.src = `/api/receipts/${receipt.id}/image`;
        thumbnailImg.alt = receipt.original_filename;
        thumbnailImg.className = 'w-16 h-16 object-cover rounded border border-border';

        const thumbnailLabel = document.createElement('span');
        thumbnailLabel.className = 'text-sm text-primary underline';
        thumbnailLabel.textContent = 'View receipt image';

        imageThumbnail.appendChild(thumbnailImg);
        imageThumbnail.appendChild(thumbnailLabel);

        imageThumbnail.addEventListener('click', () => {
          openImageLightbox(`/api/receipts/${receipt.id}/image`, receipt.vendor || receipt.original_filename);
        });

        receiptCard.appendChild(imageThumbnail);
      }

      // Receipt details grid
      const detailsGrid = document.createElement('div');
      detailsGrid.className = 'grid grid-cols-2 gap-3 text-sm';

      const fields = [
        { label: 'Vendor', value: receipt.vendor || '—' },
        { label: 'Date', value: receipt.receipt_date ? new Date(receipt.receipt_date).toLocaleDateString('en-CH') : '—' },
        { label: 'Amount', value: receipt.amount && receipt.currency ? `${receipt.currency} ${receipt.amount.toFixed(2)}` : '—' },
        { label: 'Amount (CHF)', value: receipt.amount_chf ? formatCurrency(receipt.amount_chf, 'CHF') : '—' },
        { label: 'VAT', value: receipt.vat_percent ? `${receipt.vat_percent.toFixed(1)}%` : '—' },
        { label: 'VAT Amount', value: receipt.vat_amount ? formatCurrency(receipt.vat_amount, 'CHF') : '—' },
        { label: 'Expense Type', value: receipt.expense_type || '—' },
      ];

      for (const field of fields) {
        const fieldDiv = document.createElement('div');
        const labelSpan = document.createElement('span');
        labelSpan.className = 'text-muted-foreground block';
        labelSpan.textContent = field.label;

        const valueSpan = document.createElement('span');
        valueSpan.className = 'font-medium block mt-1';
        valueSpan.textContent = field.value;

        fieldDiv.appendChild(labelSpan);
        fieldDiv.appendChild(valueSpan);
        detailsGrid.appendChild(fieldDiv);
      }

      receiptCard.appendChild(detailsGrid);

      // Notes (if any)
      if (receipt.notes) {
        const notesDiv = document.createElement('div');
        notesDiv.className = 'text-sm';
        const notesLabel = document.createElement('span');
        notesLabel.className = 'text-muted-foreground block';
        notesLabel.textContent = 'Notes';
        const notesValue = document.createElement('p');
        notesValue.className = 'mt-1';
        notesValue.textContent = receipt.notes;
        notesDiv.appendChild(notesLabel);
        notesDiv.appendChild(notesValue);
        receiptCard.appendChild(notesDiv);
      }

      receiptsContainer.appendChild(receiptCard);
    }

    receiptsSection.appendChild(receiptsContainer);
  }

  content.appendChild(receiptsSection);
  modal.appendChild(content);

  // Footer with close button
  const footer = document.createElement('div');
  footer.className = 'p-6 border-t border-border flex justify-end';

  const closeFooterButton = document.createElement('button');
  closeFooterButton.className = `
    px-4 py-2 rounded-md
    bg-primary text-primary-foreground
    hover:bg-primary/90
    transition-colors
  `;
  closeFooterButton.textContent = 'Close';
  footer.appendChild(closeFooterButton);

  modal.appendChild(footer);

  overlay.appendChild(modal);

  // Event handlers
  const handleClose = () => {
    overlay.classList.add('animate-out', 'fade-out', 'duration-150');
    setTimeout(() => {
      overlay.remove();
      document.body.style.overflow = '';
      if (onClose) onClose();
    }, 150);
  };

  closeButton.addEventListener('click', handleClose);
  closeFooterButton.addEventListener('click', handleClose);

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      handleClose();
    }
  });

  document.addEventListener('keydown', function escapeHandler(e) {
    if (e.key === 'Escape') {
      handleClose();
      document.removeEventListener('keydown', escapeHandler);
    }
  });

  // Prevent body scroll
  document.body.style.overflow = 'hidden';

  document.body.appendChild(overlay);
}

export default showExpenseReportDetailModal;
